import { Icon, Tooltip } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { MetaConversation } from 'lib-shared-legacy-types';
import { useCallback, useState } from 'react';

import { RootReducerStateT } from 'app/reducers/rootReducer';
import { TypingType } from 'components/conversation/reducers/typing';
import { WebsocketRequestMessage } from 'middleware/websocket/types/outboundWebsocketEvent';

import { ConversationListItem } from './conversationListItem';
import styles from './conversations.module.scss';

type ConversationsProps = {
  typings: RootReducerStateT['typing']['typings'];
  conversationsRemove: (csid: number) => void;
  typingRemove: (typing: TypingType) => void;
  wsRequest: (payload: WebsocketRequestMessage) => Promise<unknown>;
  isMenuAlwaysOpen?: boolean;
  conversationsLastSearchParams: RootReducerStateT['conversationsPage']['lastSearchParams'];
  isMenuOpen: RootReducerStateT['settings']['personal']['isLeftMenuOpen'];
  onToggleNavigation?: () => void;
  metaConversationsBeforeBreak: RootReducerStateT['conversations']['metaConversations'];
  metaConversationsAfterBreak: RootReducerStateT['conversations']['metaConversations'];
};

export const Conversations = (props: ConversationsProps): JSX.Element => {
  const [showAllConversations, setShowAllConversations] = useState(false);

  const toggleConversationShow = useCallback(() => {
    setShowAllConversations((prev) => !prev);
  }, []);

  const renderListItems = useCallback(
    (currentMetaConv: MetaConversation, listIndex: number) => {
      const {
        typings,
        conversationsRemove,
        typingRemove,
        wsRequest,
        isMenuOpen,
        conversationsLastSearchParams,
        onToggleNavigation,
      } = props;

      if (!currentMetaConv) {
        return null;
      }
      const conversationTyping = typings.find(
        (typing) => typing.csid === currentMetaConv.conversation.id,
      );

      return (
        <ConversationListItem
          key={currentMetaConv.conversation.id as any}
          listIndex={listIndex}
          conversationsLastSearchParams={conversationsLastSearchParams}
          isMenuOpen={isMenuOpen}
          isViewingConversation={
            window.location.pathname ===
            `${BASEPATH}conversation/${currentMetaConv.conversation.id}`
          }
          conversationsRemove={conversationsRemove}
          typingRemove={typingRemove}
          wsRequest={wsRequest}
          typing={conversationTyping}
          metaConversation={currentMetaConv}
          onToggleNavigation={onToggleNavigation}
        />
      );
    },
    [props],
  );

  const renderShowMoreOrLess = useCallback(() => {
    const { metaConversationsAfterBreak, isMenuOpen } = props;
    if (isMenuOpen) {
      return showAllConversations ? (
        <span>Show less</span>
      ) : (
        <span>Show more ({metaConversationsAfterBreak.length})</span>
      );
    }
    return (
      <Icon
        name={showAllConversations ? 'minus' : 'dots-h'}
        className={styles.showMoreOrLessIcon}
      />
    );
  }, [props, showAllConversations]);

  const {
    metaConversationsBeforeBreak,
    metaConversationsAfterBreak,
    isMenuOpen,
  } = props;

  const renderedConversations = showAllConversations
    ? metaConversationsBeforeBreak.concat(metaConversationsAfterBreak)
    : metaConversationsBeforeBreak;

  const rootClass = classnames({
    [styles.root]: true,
    [styles.rootCollapsed]: !isMenuOpen,
  });

  return (
    <ul className={rootClass}>
      {renderedConversations.map((metaConversation, index) =>
        renderListItems(metaConversation, index),
      )}

      {metaConversationsAfterBreak.length > 0 && (
        <li className={styles.listItem}>
          <Tooltip
            content={showAllConversations ? 'Show less' : 'Show more'}
            disabled={isMenuOpen}
          >
            <button
              onClick={toggleConversationShow}
              className={styles.showMoreOrLess}
            >
              {renderShowMoreOrLess()}
            </button>
          </Tooltip>
        </li>
      )}
    </ul>
  );
};
